import { Route, useLocation } from 'react-router';
import { Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MobileRouterPaths } from '../mobileAppConstants';
import { getAppFeatures, makeSelectAppLoggedIn } from '../../app/appSelectors';
import Cookies from 'universal-cookie';
import MobileMenu from './MobileMenu/MobileMenu';
import React, { useEffect, useRef } from 'react';
import { MobileContentStyled } from './styled/MobileAppStyled';
import LiteTradeProvider from '../../providers/LiteTradeProvider/LiteTradeProvider';
import MobileSliderPrivatePages from './MobileSliderPages/MobileSliderPrivatePages';
import { getLiteTradeMarketAction } from '../../modules/LiteTrade/liteTradeActions';
import MobileAppPrivateRouteConnectionError from './MobileAppPrivateRouteConnectionError';
import { getLiteTradeSocketState } from '../../modules/LiteTrade/liteTradeSelectors';
import classNames from 'classnames';
import { locationSlice } from '../../modules/Location/locationReducer';
import {
    getIsLocationInitedSelector,
    getIsLocationPlatformDesktopSelector,
    getIsLocationPlatformMobileSelector,
} from '../../modules/Location/locationSelectors';
import PageNotFound from '../../app/PageNotFound';
import { TFeaturesNames } from '../../app/types/appActions.t';
import { useTelegramContext } from "../../providers/TelegramProvider/TelegramContext";

const browserCookies = new Cookies();

interface IMobileAppPrivateRouteProps extends React.ComponentProps<typeof Route> {
    children?: React.ReactNode;
    exact?: boolean;
    feature?: TFeaturesNames;
    path: string;
    withMenu?: boolean;
    withWS?: boolean;
}

function MobileAppPrivateRoute({ feature, withWS, withMenu = true, ...props }: IMobileAppPrivateRouteProps) {
    const location = useLocation();
    const isLoggedIn = useSelector(makeSelectAppLoggedIn());
    const liteTradeSocketState = useSelector(getLiteTradeSocketState);
    const isMobilePlatform = useSelector(getIsLocationPlatformMobileSelector);
    const isLocationInited = useSelector(getIsLocationInitedSelector);
    const dispatch = useDispatch();
    const isInited = useRef(false);
    const isDesktop = useSelector(getIsLocationPlatformDesktopSelector);
    const skipPinRef = useRef(new URLSearchParams(location.search).get('skip-pin'));
    const features = useSelector(getAppFeatures);
    const { isTgAuth } = useTelegramContext();

    useEffect(() => {
        dispatch(getLiteTradeMarketAction());
    }, [dispatch]);

    // if unauthorized save originPathname to return back after auth
    useEffect(() => {
        if (isInited.current || !isLocationInited) {
            return;
        }
        isInited.current = true;

        if (!isLoggedIn || !browserCookies.get('pinOk')) {
            dispatch(locationSlice.actions.setOriginParamsAction({
                originPathname: location.pathname,
            }));
        }
    }, [dispatch, isLoggedIn, props.path, location, isLocationInited]);

    if (!isLoggedIn) {
        const data = localStorage.getItem('lastlogin_data');
        if (data) {
            return (
                <Redirect
                    to={{
                        pathname: MobileRouterPaths.login,
                        search: location.search,
                    }}
                />
            );
        }
        return (
            <Redirect
                to={{
                    pathname: MobileRouterPaths.registration,
                    search: location.search,
                }}
            />
        );

    }

    if (!browserCookies.get('pinOk') && !isDesktop && !skipPinRef.current && !isTgAuth) {
        return (
            <Redirect
                to={{
                    pathname: MobileRouterPaths.auth,
                    search: location.search,
                }}
            />
        );
    }

    if (feature && !features[feature]) {
        return <Route path="**" component={PageNotFound}/>
    }

    return (
        <>
            <LiteTradeProvider withWS={withWS}/>
            <MobileAppPrivateRouteConnectionError withWS={withWS}/>
            {(!withWS || liteTradeSocketState === 1) && (
                <MobileContentStyled
                    className={classNames({
                        'mobile-platform': isMobilePlatform,
                        'with-menu': !!withMenu,
                    })}
                >
                    <Route {...props} />
                </MobileContentStyled>
            )}
            {!!withMenu && isMobilePlatform && <MobileMenu/>}
            <MobileSliderPrivatePages/>
        </>
    );
}

export default MobileAppPrivateRoute;
